import { useMemo } from 'react';

import { bindActionCreators, useDispatch, useSelector } from 'third-party';

import { blankCheckEndpoint } from 'constants/endpoints';

import { appSelectors } from 'selectors';

import { httpMethod, simplifyBuilder } from 'utils/sra';

import { APIBlankCheck, APIBlankCheckRequest } from 'types/api';

const PAGE_SIZE = 10;

export type BlankCheckState = {
  prints: APIBlankCheck[];
  printsPage: number;
  allPrintsLoaded: boolean;
  generatePending: boolean;
  loadPrintsPending: boolean;
  loadMorePrintsPending: boolean;
};

export const blankCheckState: BlankCheckState = {
  prints: [],
  printsPage: 0,
  allPrintsLoaded: false,
  generatePending: false,
  loadPrintsPending: false,
  loadMorePrintsPending: false,
};

const builder = simplifyBuilder(blankCheckState, {});

const logBlankCheck = builder.createServerAction(
  (organizationId: string, bankId: string, model: APIBlankCheckRequest) => ({
    name: 'logBlankCheck',
    url: blankCheckEndpoint(organizationId, bankId),
    method: httpMethod.post,
    body: model,
    onSuccess: (state: BlankCheckState, payload: APIBlankCheck) => ({
      prints: [payload, ...state.prints],
    }),
  }),
);

const loadBlankChecks = builder.createServerAction(
  (organizationId: string, bankId: string) => ({
    name: 'loadBlankChecks',
    url: `${blankCheckEndpoint(
      organizationId,
      bankId,
    )}?pageNumber=0&pageSize=${PAGE_SIZE}`,
    method: httpMethod.get,
    onSuccess: (_state: BlankCheckState, payload: APIBlankCheck[]) => ({
      prints: payload,
      printsPage: 0,
      allPrintsLoaded: payload.length < PAGE_SIZE,
    }),
  }),
);

const loadMoreBlankChecks = builder.createServerAction(
  (organizationId: string, bankId: string, pageNumber: number) => ({
    name: 'loadMoreBlankChecks',
    url: `${blankCheckEndpoint(
      organizationId,
      bankId,
    )}?pageNumber=${pageNumber}&pageSize=${PAGE_SIZE}`,
    method: httpMethod.get,
    onSuccess: (state: BlankCheckState, payload: APIBlankCheck[]) => ({
      prints: [...state.prints, ...payload],
      printsPage: pageNumber,
      allPrintsLoaded: payload.length < PAGE_SIZE,
    }),
  }),
);

const clearBlankChecks = builder.createReduxAction(() => ({
  name: 'clearBlankChecks',
  updater: () => ({ prints: [], printsPage: 0, allPrintsLoaded: false }),
}));

export const useBlankCheck = () => {
  const dispatch = useDispatch();
  const organizationId = useSelector(appSelectors.organizationId);

  return useMemo(
    () =>
      bindActionCreators(
        {
          logBlankCheck: logBlankCheck.bind(null, organizationId),
          loadBlankChecks: loadBlankChecks.bind(null, organizationId),
          loadMoreBlankChecks: loadMoreBlankChecks.bind(null, organizationId),
          clearBlankChecks,
        },
        dispatch,
      ),
    [dispatch, organizationId],
  );
};

export const blankCheckReducer = builder.getReducers();
